import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
    ArrowLeft, Crown, Trophy, Loader2, Coins, Medal,
} from "lucide-react";
import UserAvatar from "../components/UserAvatar";

const CROWN_COLORS = ["#F97316", "#FFD700", "#94A3B8", "#B45309", "#0EA5E9", "#EC4899", "#8B5CF6", "#00BA7C", "#2563EB", "#FF3366"];

function crownColor(rank) {
    if (rank === 1) return "#FFD700";
    if (rank === 2) return "#C0C0C0";
    if (rank === 3) return "#CD7F32";
    return CROWN_COLORS[(rank - 1) % CROWN_COLORS.length];
}

export default function PokerLeaderboard() {
    const [season, setSeason] = useState("current");
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/poker/leaderboard?season=${season}&limit=50`)
            .then((r) => setBoard(r.data))
            .catch(() => setBoard({ rows: [], label: "" }))
            .finally(() => setLoading(false));
    }, [season]);

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/poker/tournaments" data-testid="leaderboard-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Back to tournaments
            </Link>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantyellow border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Trophy className="w-6 h-6 sm:w-7 sm:h-7 text-ink" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Endfb <span className="text-vibrantorange">Champions</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        Top 10 earn a champion badge on their profile · resets monthly
                    </p>
                </div>
            </div>

            <div className="flex gap-2 mb-4">
                <button type="button" onClick={() => setSeason("current")} data-testid="leaderboard-season-current" className={`brutal-btn text-xs py-2 px-3 ${season === "current" ? "bg-vibrantorange text-white" : "bg-white text-ink"}`}>
                    This month
                </button>
                <button type="button" onClick={() => setSeason("all")} data-testid="leaderboard-season-all" className={`brutal-btn text-xs py-2 px-3 ${season === "all" ? "bg-vibrantorange text-white" : "bg-white text-ink"}`}>
                    All-time
                </button>
            </div>

            {loading ? (
                <div className="font-heading font-bold text-ink/60 text-center py-12">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" strokeWidth={2.5} />
                </div>
            ) : !board.rows.length ? (
                <div className="brutal-card p-8 bg-cream text-center">
                    <Medal className="w-10 h-10 mx-auto text-ink/40 mb-2" strokeWidth={2} />
                    <p className="font-heading font-bold text-ink/60">
                        No tournament winners yet in {board.label}.<br />
                        <Link to="/poker/tournaments" className="text-vibrantorange hover:underline">Enter one now</Link> and be first on the board.
                    </p>
                </div>
            ) : (
                <div className="brutal-card p-4 bg-white" data-testid="leaderboard-table">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/50 mb-2">{board.label} · {board.rows.length} paid</div>
                    <div className="space-y-1">
                        {board.rows.map((row) => {
                            const isTop10 = row.rank <= 10;
                            return (
                                <Link
                                    key={row.user_id}
                                    to={`/u/${row.username}`}
                                    data-testid={`leaderboard-row-${row.rank}`}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all hover:bg-cream ${row.rank <= 3 ? "bg-vibrantyellow/30" : ""}`}
                                >
                                    <div className="w-8 text-center flex items-center justify-center">
                                        {row.rank <= 3 ? (
                                            <Crown className="w-5 h-5" strokeWidth={2.5} fill={crownColor(row.rank)} color={crownColor(row.rank)} />
                                        ) : (
                                            <span className="font-heading font-black text-lg text-ink/40 tabular-nums">{row.rank}</span>
                                        )}
                                    </div>
                                    <UserAvatar username={row.username} size={36} className="!border-2 !shadow-none" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-heading font-bold text-sm text-ink truncate">{row.username}</span>
                                            {isTop10 && <Crown className="w-3.5 h-3.5" strokeWidth={2.5} fill={crownColor(row.rank)} color={crownColor(row.rank)} />}
                                        </div>
                                        <div className="font-body text-xs text-ink/50">{row.wins} win{row.wins === 1 ? "" : "s"} · {row.cashes} cash{row.cashes === 1 ? "" : "es"}</div>
                                    </div>
                                    <div className="flex items-center gap-1 text-vibrantorange">
                                        <Coins className="w-3.5 h-3.5" strokeWidth={2.5} />
                                        <span className="font-heading font-black tabular-nums">{row.total_won}</span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
